/* eslint-disable @typescript-eslint/no-explicit-any */
import { Button } from "components/ui/button";
import Table from "../components/table";

import { featureFlags } from "@/config/feature-flag";
import { CONCILIATION_STORAGE_KEYS, NSU_TYPES } from "@/constants/conciliation-storage";
import { StatusEnumStoreConciliation } from "@/enum/statusConciliation";
import {
  useGetNsuQuery,
  usePublicGetNsuQuery,
} from "@/services/conciliation/nsu";
import {
  usePublicSendEmailFinalizeNsuEvidenceMutation,
  useSendEmailFinalizeNsuEvidenceMutation,
} from "@/services/email";
import { useOperatorMePublicQuery, useOperatorMeQuery } from "@/services/user";
import { handeOrderBy } from "@/utils/pagination";
import { ContentContainer, PageLayout, PageTitle } from "components/layout";
import { Load } from "components/load";
import { QueryErrorCard } from "components/query-error-card";
import { toast } from "components/ui/use-toast";
import { format } from "date-fns";
import React, { useEffect, useMemo, useRef } from "react";
import { useForm } from "react-hook-form";
import { IoRefresh } from "react-icons/io5";
import { useNavigate } from "react-router-dom";
import { IEvidenceRegister } from "src/@types/evidence-register";
import CardMobile from "../components/card-mobile";
import { useFilterState } from "../ducks/filter/hooks/filterState";
import { useColumns } from "./columns";
import { handleOrderByEvidence } from "./utils";

export const NsuRegister: React.FC = () => {
  const methods = useForm();
  const [isUploading, setIsUploading] = React.useState(false);
  const [isFinalizing, setIsFinalizing] = React.useState(false);
  const hasNavigatedRef = useRef(false);

  const columns = useColumns({ methods, isUploading, setIsUploading });

  const useKeycloak = featureFlags.USE_KEYCLOAK;

  const { data: storePrivate, isLoading: isLoadingStorePrivate, error: storeErrorPrivate } = useOperatorMeQuery(undefined, {
    skip: useKeycloak,
  });
  const { data: storePublic, isLoading: isLoadingStorePublic, error: storeErrorPublic } = useOperatorMePublicQuery(undefined, {
    skip: !useKeycloak,
  });

  const store = useKeycloak ? storePublic : storePrivate;
  const isLoadingStore = useKeycloak ? isLoadingStorePublic : isLoadingStorePrivate;
  const storeError = useKeycloak ? storeErrorPublic : storeErrorPrivate;

  const navigate = useNavigate();

  const sendEmailNsuKeycloak = useKeycloak
    ? usePublicSendEmailFinalizeNsuEvidenceMutation()
    : useSendEmailFinalizeNsuEvidenceMutation();

  const [sendEmailFinalizeNsuEvidence, { isLoading: isLoadingFinlize }] =
    sendEmailNsuKeycloak;

  const filterState = useFilterState();
  const { filters } = filterState || { filters: {} };
  const orderBy = handeOrderBy(filters.order);

  const nsuType = localStorage.getItem(CONCILIATION_STORAGE_KEYS.NSU_TYPE);
  const isRejected = localStorage.getItem(CONCILIATION_STORAGE_KEYS.REJECTED) === "true";
  const solicitationId = localStorage.getItem(CONCILIATION_STORAGE_KEYS.SOLICITATION_ID) ?? "";

  const storeData = store as any;
  const conciliationStoreId = storeData?.conciliation_store_id ?? "";
  const storeCodeFromStore = storeData?.code ?? "";
  const conciliationDate = storeData?.conciliation_date;

  const objectNsu = useMemo(() => ({
    conciliation_id: conciliationStoreId,
    store_code: storeCodeFromStore,
    isNumberEmpty: nsuType === NSU_TYPES.EVIDENCE,
    solicitation_id: solicitationId,
    is_rejected: isRejected ? "true" : undefined,
    orderBy: JSON.stringify(orderBy),
    meta: {
      page: filters?.page ?? 1,
    },
  }), [conciliationStoreId, storeCodeFromStore, nsuType, solicitationId, isRejected, orderBy, filters?.page]);

  const getNsuKeycloak = useKeycloak
    ? usePublicGetNsuQuery(objectNsu)
    : useGetNsuQuery(objectNsu);

  const {
    data: response,
    isLoading: isLoadingDivergence,
    refetch,
    isFetching,
    error: nsuError,
  } = getNsuKeycloak;

  const nsuData = response?.data ?? [];
  const firstItem = nsuData[0];
  const conciliation = firstItem?.conciliation;
  const conciliationStatus = conciliation?.status;
  const storeCode = String(conciliation?.store?.code ?? "");

  useEffect(() => {
    if (
      !hasNavigatedRef.current &&
      conciliationStatus === StatusEnumStoreConciliation.CLOSED
    ) {
      hasNavigatedRef.current = true;
      navigate("/informations?is-closed=true", { replace: true });
    }
  }, [conciliationStatus, navigate]);

  const data = useMemo(() => {
    if (orderBy && Object.keys(orderBy)?.length) {
      const key = Object.keys(orderBy)[0];
      if (key === "evidence") {
        return handleOrderByEvidence(nsuData, orderBy[key]);
      }
    }
    return nsuData;
  }, [response?.data, orderBy]);

  const handleFinalizeEvidence = async () => {
    setIsFinalizing(true);
    try {
      await sendEmailFinalizeNsuEvidence({
        conciliation_id: conciliationStoreId?.toString() ?? "",
        store_code: storeCode,
        solicitation_id: solicitationId,
      }).unwrap();
      navigate(`/informations?success=true&type=nsu&nsuType=${nsuType}&clear-storage=true`);
    } catch (e) {
      toast({
        variant: "destructive",
        description: "Erro ao finalizar, comunique o administrador!",
        duration: 5000,
      });
      setIsFinalizing(false);
    }
  };

  const isLoading = isLoadingStore || isLoadingDivergence;
  const isSubmitting = isFinalizing || isLoadingFinlize;

  const formattedConciliationDate = conciliationDate
    ? format(new Date(conciliationDate), "dd/MM/yyyy")
    : "";

  if (isLoading) {
    return <Load />;
  }

  const queryError = storeError || nsuError;
  if (queryError) {
    return (
      <QueryErrorCard
        title="Erro ao carregar Registro de NSU"
        description="Não foi possível carregar a tela de registro de NSU. Por favor, tente novamente ou solicite um novo link por e-mail."
        onRetry={refetch}
        isRetrying={isFetching}
      />
    );
  }

  return (
    <PageLayout>
      <div className="flex-1 w-full flex justify-between items-start">
        <div>
          <PageTitle
            title={
              nsuType === NSU_TYPES.EVIDENCE
                ? "Lançamento de Depósitos"
                : "Comprovante de Depósitos"
            }
            description={`Loja ${storeCode}`}
          />
          <p className="text-muted-foreground text-sm mt-1">
            {nsuType === NSU_TYPES.EVIDENCE
              ? "Verifique os lançamentos sem NSU vinculada, realize o vínculo e envie os comprovantes."
              : "Envie os comprovantes de depósitos realizados nesta data."}
          </p>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <div className="text-sm text-right">
            <span className="font-bold">Conciliação:</span>{" "}
            {formattedConciliationDate}
          </div>
          <span
            className={`flex items-center gap-1.5 text-[#3c7dd9] text-sm cursor-pointer hover:underline ${isFetching ? "cursor-not-allowed" : ""}`}
            onClick={refetch}
          >
            <IoRefresh className={`text-lg ${isFetching ? "animate-spin" : ""}`} />
            Recarregar
          </span>
        </div>
      </div>
      <ContentContainer>
        <div className="hidden w-full md:flex md:flex-col">
          <Table
            columns={columns}
            data={data as unknown as IEvidenceRegister[]}
            isFetching={isFetching}
          />
        </div>
        <div className="flex flex-col gap-3 w-full md:hidden">
          {data?.map((item, index) => (
            <CardMobile
              key={item?.id ?? index}
              methods={methods}
              id={item?.id as number}
              price={item?.amount as unknown as number}
              entity_name="NSU"
              number={item?.number}
              validated={item?.validated}
              is_loss={false}
              is_payroll_discount={false}
            />
          ))}
        </div>
        <div className="flex w-full justify-end pt-4">
          <Button
            isLoading={isSubmitting}
            disabled={isSubmitting}
            onClick={handleFinalizeEvidence}
          >
            Finalizar
          </Button>
        </div>
      </ContentContainer>
    </PageLayout>
  );
};
